import { createContext, useContext, useState, useEffect } from "react";
import { decodeJwt } from "../utils/decodeJwt";

export const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem("token") || "");
  const [refreshToken, setRefreshToken] = useState(() => localStorage.getItem("refreshToken") || "");
  const [credits, setCredits] = useState(0);
  const [expirationTimeout, setExpirationTimeout] = useState(null);

  // Derivar isAdmin, isPaid e email do JWT (assinado pelo servidor)
  const tokenPayload = token ? decodeJwt(token) : null;
  const isAdmin = tokenPayload?.isAdmin || false;
  const isPaid = tokenPayload?.isPaid || false;
  const email = tokenPayload?.email || "";

  function login({ token, refreshToken }) {
    setToken(token);
    setRefreshToken(refreshToken);

    localStorage.setItem("token", token);
    localStorage.setItem("refreshToken", refreshToken);

    startExpirationTimerFromToken(token);
  }

  async function fetchCredits(tkn) {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/user/status`, {
        headers: { Authorization: `Bearer ${tkn || token}` },
      });
      if (response.status === 401 || response.status === 403) {
        logout();
        return;
      }
      if (response.ok) {
        const data = await response.json();
        setCredits(data.credits || 0);
      }
    } catch { /* silently fail */ }
  }

  function logout() {
    setToken("");
    setRefreshToken("");
    setCredits(0);
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    if (expirationTimeout) {
      clearTimeout(expirationTimeout);
      setExpirationTimeout(null);
    }
  }

  async function refreshAccessToken() {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/refresh-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        setToken(data.token);
        localStorage.setItem("token", data.token);
        startExpirationTimerFromToken(data.token);
      } else {
        logout();
      }
    } catch {
      logout();
    }
  }

  function startExpirationTimerFromToken(token) {
    const payload = decodeJwt(token);
    if (!payload?.exp) return;

    const expirationTimeMs = payload.exp * 1000;
    const now = Date.now();
    const timeLeft = expirationTimeMs - now;

    if (timeLeft <= 0) {
      refreshAccessToken();
      return;
    }

    if (expirationTimeout) clearTimeout(expirationTimeout);

    const timeout = setTimeout(() => {
      refreshAccessToken();
    }, timeLeft - 60000); // Renova 1min antes de expirar

    setExpirationTimeout(timeout);
  }

  useEffect(() => {
    if (token) {
      startExpirationTimerFromToken(token);
      fetchCredits(token);
    }
  }, [token]);

  // Verifica sessao a cada 60s para detectar login em outro dispositivo
  useEffect(() => {
    if (!token) return;
    const interval = setInterval(() => {
      fetchCredits(token);
    }, 60000);
    return () => clearInterval(interval);
  }, [token]);

  return (
    <AuthContext.Provider value={{ token, isAdmin, email, isPaid, credits, fetchCredits, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
